import { useCallback, useMemo, useState } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import { Business, type BusinessSubscription } from "lens-platform-sdk";

import { getPlanName, isAppIQProPlan } from "src/services/plans";
import { useUpdateBusinessSubscriptionCustomField } from "src/hooks/useUpdateBusinessSubscriptionCustomField";
import { useTrackedTableSort } from "src/hooks/useTrackedTableSort";

import Button from "src/components/Button/TrackedButton";
import LinkButton from "src/components/Button/TrackedLinkButton";
import Table from "src/components/Table/Table";
import { dateColDef } from "src/components/TableCells/DateCell";
import { stateColDef } from "src/components/TableCells/StateCell";
import { subscriptionIdColDef } from "src/components/TableCells/SubscriptionIdCell";
import ActionsCell, { actionsColDef } from "src/components/TableCells/ActionsCell";
import QuantityCell, { quantityMeta } from "src/components/TableCells/QuantityCell";

import AssignLensAppIQModal from "./AssignLensAppIQModal";

const caption = "Lens AppIQ Subscriptions";
const addNodesLabel = "Add Nodes";
const noDataText = "No Lens AppIQ Subscriptions for your Lens Business ID!";
const nodesLockedText =
  "Cannot change nodes for expired subscriptions or this subscription is not auto renewable. Please contact your sales representative to modify the quantity.";

const columnHelper = createColumnHelper<BusinessSubscription>();

interface Props {
  loading?: boolean;
  businessSubscriptions: BusinessSubscription[];
  business: Business | undefined;
}

const AppIQSubscriptionTable = ({ loading, businessSubscriptions, business }: Props) => {
  const {
    updateBusinessSubscriptionCustomField,
    errorUpdatingBusinessSubscriptionCustomField: customFieldUpdateError,
    updatingBusinessSubscriptionCustomField: updatingCustomField,
  } = useUpdateBusinessSubscriptionCustomField();

  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });
  const businessId = business?.id;

  const [currentLensAppIQSubs, setCurrentLensAppIQSubs] = useState<BusinessSubscription | null>(null);
  const closeAssignLensAppIQModal = () => {
    setCurrentLensAppIQSubs(null);
  };
  const openAssignLensAppIQModal = (subscription: BusinessSubscription) => {
    setCurrentLensAppIQSubs(subscription);
  };
  const onAssignLensAppIQ = useCallback(
    async (subscription: BusinessSubscription, orgId: string) => {
      if (!businessId) {
        return;
      }

      return updateBusinessSubscriptionCustomField(
        {
          businessId,
          businessSubscriptionId: subscription.id,
          customField: { name: "lensAppsCloudOrgId", value: orgId || "" },
        },
        {
          onSuccess: (d) => {
            subscription.customFields = d.customFields;
            closeAssignLensAppIQModal();
          },
        },
      );
    },
    [businessId, updateBusinessSubscriptionCustomField],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", { ...subscriptionIdColDef }),
      columnHelper.accessor("planCode", {
        header: () => <span>Plan</span>,
        cell: ({ getValue }) => <span>{getPlanName(getValue())}</span>,
      }),
      columnHelper.accessor("currentPeriodEndsAt", {
        ...dateColDef,
        header: () => <span>Next Renewal</span>,
      }),
      columnHelper.accessor("seats", {
        meta: quantityMeta,
        header: () => <span>Nodes</span>,
        cell: (props) => {
          const subscription = props.row.original;

          const isActive = !business?.external && subscription.state === "active";
          const isSubscriptionUpgradable = isAppIQProPlan(subscription.planCode) && isActive;

          return <QuantityCell {...props} isUpgradable={isSubscriptionUpgradable} lockedText={nodesLockedText} />;
        },
      }),
      columnHelper.accessor("state", {
        ...stateColDef,
        header: () => <span>Status</span>,
      }),
      columnHelper.accessor("id", {
        ...actionsColDef,
        header: () => <span>Actions</span>,
        cell: ({ row }) => {
          const subscription = row.original;

          const isActive = !business?.external && subscription.state === "active";
          const isSubscriptionUpgradable = isAppIQProPlan(subscription.planCode) && isActive;

          return (
            <ActionsCell>
              <LinkButton
                label={addNodesLabel}
                to={`/business/${businessId}/subscriptions/${subscription.id}`}
                disabled={!isSubscriptionUpgradable}
                size="xs"
                discreet
                primary
              />
              <Button
                label="Manage"
                aria-label="Manage Organization"
                onClick={() => openAssignLensAppIQModal(subscription)}
                size="xs"
                discreet
                primary
                disabled={!isActive}
              />
            </ActionsCell>
          );
        },
      }),
    ],
    [business?.external, businessId],
  );

  const table = useReactTable({
    data: businessSubscriptions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange,
  });
  const tableRowModel = table.getRowModel();
  const tableRows = useMemo(() => tableRowModel.rows, [tableRowModel]);

  return (
    <>
      <Table<BusinessSubscription>
        caption={caption}
        rows={tableRows}
        columns={table.getAllColumns()}
        headerGroups={table.getHeaderGroups()}
        loading={loading}
        noDataText={noDataText}
        borderless={false}
      />
      {currentLensAppIQSubs && (
        <AssignLensAppIQModal
          open={Boolean(currentLensAppIQSubs)}
          assigningOrgId={updatingCustomField}
          disabled={updatingCustomField}
          onModalClose={closeAssignLensAppIQModal}
          subscription={currentLensAppIQSubs}
          initialOrgId={
            currentLensAppIQSubs.customFields?.find(({ name }) => name === "lensAppsCloudOrgId")?.value ?? null
          }
          onClickAssign={onAssignLensAppIQ}
          assignOrgIdError={customFieldUpdateError}
        />
      )}
    </>
  );
};

export default AppIQSubscriptionTable;
